package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.getPlatform
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberFeedbackDebugInfo
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.FeedbackEmail
import com.shadowings.gero.util.FeedbackType
import com.shadowings.gero.util.rememberFeedbackSender
import com.shadowings.gero.util.rememberShareManager
import compose.icons.FeatherIcons
import compose.icons.feathericons.Flag
import compose.icons.feathericons.Share2
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.correct_feedback_1
import gero.composeapp.generated.resources.correct_feedback_2
import gero.composeapp.generated.resources.correct_feedback_3
import gero.composeapp.generated.resources.feedback_quiz_error_subject
import gero.composeapp.generated.resources.feedback_quiz_error_template
import gero.composeapp.generated.resources.scroll_down_desc
import gero.composeapp.generated.resources.share_btn_text
import gero.composeapp.generated.resources.share_title
import gero.composeapp.generated.resources.wrong_feedback_1
import gero.composeapp.generated.resources.wrong_feedback_2
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

@Composable
fun getBottomButtonHeight(): Dp {
    val deviceInfo = rememberDeviceInfo()
    val buttonHeight = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 36.dp
        DeviceClass.PHONE_MEDIUM -> 48.dp
        else -> 64.dp
    }
    return buttonHeight
}

@Composable
fun getBottomButtonPadding(): Dp {
    val deviceInfo = rememberDeviceInfo()
    val padding = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 4.dp
        DeviceClass.PHONE_MEDIUM -> 8.dp
        else -> 16.dp
    }
    return padding
}

@Composable
fun QuizBottomFeedbackAndButton(
    state: QuizPageState,
    buttonEnabled: Boolean,
    info: String? = null,
    correctAnswer: String? = null,
    correctAnswerIsHieroglyph: Boolean = false,
    userData: UserData,
    quizInfo: String = "",
    userAnswer: String = "",
    lessonId: String = "",
    modifier: Modifier = Modifier,
    shareText: String? = null,
    shareImageProvider: (suspend () -> ImageBitmap?)? = null,
    onConfirmClick: () -> Boolean
) {
    val sendFeedback = rememberFeedbackSender()
    val quizErrorSubject = stringResource(Res.string.feedback_quiz_error_subject)
    val quizErrorTemplate = stringResource(Res.string.feedback_quiz_error_template)
    val shareManager = rememberShareManager()
    val shareTitle = stringResource(Res.string.share_title)
    val coroutineScope = rememberCoroutineScope()
    val wrongFeedback = listOf(
        stringResource(Res.string.wrong_feedback_1),
        stringResource(Res.string.wrong_feedback_2)
    )
    val correctFeedback = listOf(
        stringResource(Res.string.correct_feedback_1),
        stringResource(Res.string.correct_feedback_2),
        stringResource(Res.string.correct_feedback_3)
    )
    val shareButtonTag = stringResource(Res.string.share_btn_text)
    val feedbackButtonTag = stringResource(Res.string.scroll_down_desc)
    val wrongText = rememberSaveable(state.quizId) { wrongFeedback.random() }
    val correctText = rememberSaveable(state.quizId) { correctFeedback.random() }
    val feedbackCharacterName = rememberSaveable(state.quizId) {
        if ((1..100).random() <= 30) Characters.Nilo.name else Characters.Alyn.name
    }
    val feedbackCharacter = remember(feedbackCharacterName) {
        Characters.valueOf(feedbackCharacterName)
    }

    val deviceInfo = rememberDeviceInfo()
    val buttonHeight = getBottomButtonHeight()
    val padding = getBottomButtonPadding()

    Box(
        modifier = modifier.fillMaxWidth().padding(bottom = padding)
    ) {
        val isCorrectText = if (state.correct) "Giusta (Correct)" else "Sbagliata (Incorrect)"
        val quizErrorExtra = """
            Lesson ID: $lessonId

            $quizInfo

            User Answer: $userAnswer
            Result: $isCorrectText
        """.trimIndent()
        val feedbackDebugInfo = rememberFeedbackDebugInfo(FeedbackType.QUIZ_ERROR, extra = quizErrorExtra)
        AnimatedVisibility(
            visible = state.showResult,
            enter = fadeIn() + slideInVertically(initialOffsetY = { it }),
            exit = slideOutVertically(targetOffsetY = { it }),
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter)
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .background(MaterialTheme.colorScheme.background)
                    .align(Alignment.BottomCenter),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(Modifier.fillMaxWidth().height(1.dp).background(Color.LightGray))
                Box {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(4.dp),
                        horizontalArrangement = Arrangement.End,
                    ) {
                        // Not on web: the JS ShareManager can only copy the caption text (no
                        // native share sheet), a degraded experience compared to Android/iOS's
                        // real share sheet.
                        if (!getPlatform().isWeb) {
                            Box(
                                modifier = Modifier.size(48.dp).clickable {
                                    coroutineScope.launch {
                                        val image = try {
                                            shareImageProvider?.invoke()
                                        } catch (t: Throwable) {
                                            null
                                        }
                                        val finalShareText = shareText
                                            ?: "MiuLingo - Learn Egyptian Hieroglyphs! https://www.emmanuelevilla.com/miulingo"
                                        shareManager(finalShareText, image, shareTitle)
                                    }
                                }.semantics { testTag = shareButtonTag },
                            ) {
                                Icon(
                                    imageVector = FeatherIcons.Share2,
                                    contentDescription = stringResource(Res.string.share_btn_text),
                                    tint = MaterialTheme.colorScheme.primary,
                                    modifier = Modifier.size(24.dp).align(Alignment.Center)
                                )
                            }
                            Spacer(Modifier.width(4.dp))
                        }
                        Box(
                            modifier = Modifier.size(48.dp).clickable {
                                try {
                                    sendFeedback(
                                        FeedbackEmail(
                                            subject = quizErrorSubject,
                                            templateBody = quizErrorTemplate,
                                            debugInfo = feedbackDebugInfo
                                        )
                                    )
                                } catch (e: Exception) {
                                    println("Failed to open mail client: ${e.message}")
                                }
                            }.semantics { testTag = feedbackButtonTag },
                        ) {
                            Icon(
                                imageVector = FeatherIcons.Flag,
                                contentDescription = stringResource(Res.string.scroll_down_desc),
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(24.dp).align(Alignment.Center)
                            )
                        }
                    }
                    Box(
                        Modifier.fillMaxWidth().then(
                            if (deviceInfo.isLandscape) {
                                Modifier.padding(end = 48.dp + 4.dp + 48.dp)
                            } else {
                                Modifier
                            }
                        )
                    ) {
                        FeedbackBox(
                            character = feedbackCharacter,
                            face = if (state.correct) Faces.Happy else Faces.Sad,
                            text = if (!state.correct) wrongText else correctText,
                            userData = userData,
                            correctAnswer = if (!state.correct) correctAnswer else null,
                            correctAnswerIsHieroglyph = correctAnswerIsHieroglyph,
                        )
                    }
                }

                if (!info.isNullOrBlank()) {
                    Text(
                        modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp, top = 4.dp),
                        text = info,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurface,
                        textAlign = TextAlign.Center
                    )
                }
                Spacer(Modifier.height(buttonHeight + 8.dp))
            }
        }


        Row(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.weight(1f)
            ) {
                QuizBottomButton(
                    modifier = Modifier.height(buttonHeight).fillMaxWidth().align(Alignment.Center),
                    pending = state.pending,
                    correct = state.correct,
                    enabled = buttonEnabled,
                ) {
                    state.onBottomButtonClick {
                        onConfirmClick()
                    }
                }
            }
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuizBottomFeedbackAndButtonPreview() {
    AppTheme {
        val pagerState = androidx.compose.foundation.pager.rememberPagerState { 1 }
        val quizResults = remember { mutableStateOf(emptyMap<String, Boolean>()) }
        val quizList =
            remember { mutableStateOf(emptyList<com.shadowings.gero.domain.model.IQuizContent>()) }
        val comboCount = remember { mutableStateOf(0) }
        val errorsCount = remember { mutableStateOf(0) }
        val state = rememberQuizPageState(
            quizId = "preview",
            page = 0,
            pagerState = pagerState,
            navController = androidx.navigation.compose.rememberNavController(),
            quizResults = quizResults,
            quizList = quizList,
            comboCount = comboCount,
            errorsCount = errorsCount,
            previewMode = true
        )
        QuizBottomFeedbackAndButton(
            state = state,
            buttonEnabled = true,
            info = "Suggerimento",
            correctAnswer = "Aha",
            userData = UserData(experience = 5),
            onConfirmClick = { true }
        )
    }
}
